// Viewport selection interactions: hover highlight + click-to-select over the
// viewport canvas. Picking is read-only against the last rendered LayoutResult
// (see ViewportPickContext) — it never recomputes layout.

import { pickScene } from '../scene/picking.js';
import type { SceneNodeId } from '../scene/sceneState.js';
import type { ViewportMode, ViewportTarget } from '../state/appState.js';
import { screenToWorld } from './viewportCoords.js';
import type { ViewportOverlayController } from './viewportOverlay.js';
import type { ViewportPickContext } from './renderLoop.js';

interface ViewportSelectionDeps {
  overlay: ViewportOverlayController;
  getPickContext: () => ViewportPickContext | null;
  /** Handle a single click: select, drill into a component, or miss-step out. */
  handleViewportClick: (hit: ViewportClickHit) => void;
}

interface HitProbe {
  hasContext: boolean;
  nodeId: SceneNodeId | null;
  ownerComponentId: string | null;
  pickTarget: ViewportTarget | null;
  mode: ViewportMode;
}

export interface ViewportClickHit {
  pickedNodeId: SceneNodeId | null;
  ownerComponentId: string | null;
  pickTarget: ViewportTarget | null;
  mode: ViewportMode;
}

export function initViewportSelection(deps: ViewportSelectionDeps): void {
  const stage = document.getElementById('viewport-stage');
  if (!stage) return;

  function hitAt(clientX: number, clientY: number): HitProbe {
    const ctx = deps.getPickContext();
    if (!ctx) return { hasContext: false, nodeId: null, ownerComponentId: null, pickTarget: null, mode: 'scene' };
    const world = screenToWorld(clientX, clientY, ctx.resolution);
    if (!world) {
      return {
        hasContext: true,
        nodeId: null,
        ownerComponentId: ctx.ownerComponentId,
        pickTarget: ctx.pickTarget,
        mode: ctx.mode,
      };
    }
    const hit = pickScene(ctx.scene, ctx.layoutMap, world, { includeFrames: true });
    return {
      hasContext: true,
      nodeId: hit?.nodeId ?? null,
      ownerComponentId: ctx.ownerComponentId,
      pickTarget: ctx.pickTarget,
      mode: ctx.mode,
    };
  }

  stage.addEventListener('mousemove', e => {
    const { nodeId } = hitAt(e.clientX, e.clientY);
    deps.overlay.setHovered(nodeId);
    (stage as HTMLElement).style.cursor = nodeId ? 'pointer' : '';
  });

  stage.addEventListener('mouseleave', () => {
    deps.overlay.setHovered(null);
    (stage as HTMLElement).style.cursor = '';
  });

  // Figma-style drill with single clicks (no double-click / timing guesswork):
  // a click selects the node; clicking the already-selected component enters
  // it. A true viewport miss can still step back out from a component, but
  // filled background layers are ordinary selectable nodes, not navigation.
  stage.addEventListener('click', e => {
    const { hasContext, nodeId, ownerComponentId, pickTarget, mode } = hitAt(e.clientX, e.clientY);
    if (!hasContext) return;
    deps.handleViewportClick({ pickedNodeId: nodeId, ownerComponentId, pickTarget, mode });
  });
}
